// Clés d'une app et code d'intégration : clé publique et secret d'identité
// (renouvelables), clés secrètes serveur (montrées une seule fois, révocables).
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { AppDetail, SecretKey } from '@feedbaacks/sdk'
import { absoluteDate, Button, errorMessage, Icons, IconButton, Spinner, useFeedbaacks } from '@feedbaacks/ui'
import { d, locale } from '../i18n'

function Secret({ value, label, testId }: { value: string; label: string; testId: string }): JSX.Element {
  const { notify } = useFeedbaacks()
  const [shown, setShown] = useState(false)
  return (
    <div class="dash-secret">
      <code class="fb-mono" data-testid={testId}>{shown ? value : '•'.repeat(24)}</code>
      <Button variant="ghost" onClick={() => setShown((v) => !v)}>{shown ? d('keys_hide') : d('keys_show')}</Button>
      <IconButton label={`${d('keys_copy')} : ${label}`} onClick={() => void navigator.clipboard?.writeText(value).then(() => notify('success', d('keys_copied')), () => {})}>
        <Icons.Copy size={14} />
      </IconButton>
    </div>
  )
}

export function Keys({ appKey, apiUrl }: { appKey: string; apiUrl: string }): JSX.Element {
  const { client, notify, confirm } = useFeedbaacks()
  const [app, setApp] = useState<AppDetail | null>(null)
  const [keys, setKeys] = useState<SecretKey[]>([])
  const [label, setLabel] = useState('')
  const [fresh, setFresh] = useState<string | null>(null)
  const fail = (err: unknown) => notify('error', errorMessage(err, d('error_generic')))

  useEffect(() => {
    client.apps.get(appKey).then(setApp, fail)
    client.apps.secretKeys.list(appKey).then(setKeys, fail)
  }, [client, appKey])
  if (!app) return <div class="dash-center"><Spinner size={24} /></div>

  const rotate = async () => {
    if (!(await confirm({ title: d('keys_rotate_title'), message: d('keys_rotate_message'), confirmLabel: d('keys_rotate'), destructive: true }))) return
    client.apps.rotatePublicKeys(appKey).then(setApp, fail)
  }
  const create = async () => {
    try {
      const r = await client.apps.secretKeys.create(appKey, label.trim())
      setFresh(r.secret_key)
      setLabel('')
      setKeys(await client.apps.secretKeys.list(appKey))
    } catch (err) {
      fail(err)
    }
  }
  const revoke = async (k: SecretKey) => {
    if (!(await confirm({ title: d('keys_revoke_title'), message: d('keys_revoke_message'), confirmLabel: d('keys_revoke'), destructive: true }))) return
    try {
      await client.apps.secretKeys.revoke(appKey, k.id)
      setKeys(await client.apps.secretKeys.list(appKey))
    } catch (err) {
      fail(err)
    }
  }

  const widgetSnippet = `<script src="${apiUrl}/widget.js" async></script>
<script>
  window.Feedbaacks = window.Feedbaacks || { q: [], init() { this.q.push(['init', arguments]) } }
  Feedbaacks.init({
    publishableKey: '${app.publishable_key}',
    identityEndpoint: '/api/feedbaacks-identity', // votre serveur rend { user, signature }
    locale: '${locale}',
  })
</script>`
  const signSnippet = `import { createHmac } from 'node:crypto'
// user = la personne connectée sur votre site
const signature = createHmac('sha256', process.env.FEEDBAACKS_IDENTITY_SECRET)
  .update(user.id + '\\n' + (user.email ?? '').trim().toLowerCase())
  .digest('hex')
res.json({ user: { id: user.id, name: user.name, email: user.email }, signature })`

  return (
    <div class="dash-page" data-testid="dash-keys">
      <section class="dash-card">
        <div class="dash-field"><span>{d('keys_publishable')}</span>
          <code class="fb-mono" data-testid="dash-publishable-key">{app.publishable_key}</code>
        </div>
        <div class="dash-field"><span>{d('keys_identity_secret')}</span>
          <Secret value={app.identity_secret} label={d('keys_identity_secret')} testId="dash-identity-secret" />
        </div>
        <div><Button variant="danger" onClick={() => void rotate()} testId="dash-rotate">{d('keys_rotate')}</Button></div>
      </section>

      <section class="dash-card">
        <h2 class="fb-section-title">{d('keys_secret_title')}</h2>
        {fresh && (
          <div class="dash-fresh" role="status" data-testid="dash-fresh-secret">
            <p>{d('keys_secret_once')}</p>
            <Secret value={fresh} label={d('keys_secret_title')} testId="dash-fresh-secret-value" />
          </div>
        )}
        <ul class="dash-list">
          {keys.map((k) => (
            <li key={k.id} class={k.revoked_at ? 'is-revoked' : undefined} data-testid="dash-secret-key">
              <span class="fb-strong">{k.label || '-'}</span>
              <span class="fb-faint">{absoluteDate(k.created_at, locale)}</span>
              <span class="fb-faint">
                {k.revoked_at ? d('keys_secret_revoked') : k.last_used_at ? d('keys_secret_last_used', { date: absoluteDate(k.last_used_at, locale) }) : d('keys_secret_never_used')}
              </span>
              <span class="fb-grow" />
              {!k.revoked_at && <Button variant="ghost" onClick={() => void revoke(k)}>{d('keys_revoke')}</Button>}
            </li>
          ))}
        </ul>
        <form class="dash-inline-form" onSubmit={(e) => {
          e.preventDefault()
          void create()
        }}>
          <input class="fb-input" placeholder={d('keys_secret_label')} aria-label={d('keys_secret_label')} value={label} onInput={(e) => setLabel(e.currentTarget.value)} />
          <Button type="submit" testId="dash-create-secret">{d('keys_secret_create')}</Button>
        </form>
      </section>

      <section class="dash-card">
        <h2 class="fb-section-title">{d('keys_snippet')}</h2>
        <p class="fb-muted">{d('keys_snippet_widget')}</p>
        <pre class="dash-code-block">{widgetSnippet}</pre>
        <p class="fb-muted">{d('keys_snippet_sign')}</p>
        <pre class="dash-code-block">{signSnippet}</pre>
      </section>
    </div>
  )
}
